import React, { useState } from 'react';
import { X, BookOpen } from 'lucide-react';
import { FLOW_TEMPLATES } from './flowTemplates';
import type { FlowTemplate } from './flowTemplates';

interface TemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: FlowTemplate) => void;
}

const CATEGORIES = [
  { id: 'all', label: 'Todos', icon: '📚' },
  { id: 'regularization', label: 'Regularização', icon: '📋' },
  { id: 'customer-service', label: 'Atendimento', icon: '🔀' },
  { id: 'survey', label: 'Pesquisas', icon: '📊' },
  { id: 'notification', label: 'Notificações', icon: '🔔' },
  { id: 'other', label: 'Outros', icon: '📁' },
];

export const TemplateLibraryModal: React.FC<TemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!isOpen) return null;

  const filteredTemplates = selectedCategory === 'all'
    ? FLOW_TEMPLATES
    : FLOW_TEMPLATES.filter((t) => t.category === selectedCategory);

  const handleSelectTemplate = (template: FlowTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Biblioteca de Templates
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 px-6 pt-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`
                flex items-center gap-2 px-4 py-2 rounded-t-lg whitespace-nowrap
                transition-colors
                ${selectedCategory === cat.id
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }
              `}
            >
              <span>{cat.icon}</span>
              <span className="font-medium">{cat.label}</span>
              {cat.id === 'all' && (
                <span className="ml-1 text-xs bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                  {FLOW_TEMPLATES.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum template encontrado nesta categoria.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-4xl">{template.icon}</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {template.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        {template.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-500">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">{template.flow.nodes.length}</span>
                          nós
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">{template.flow.edges.length}</span>
                          conexões
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                    <button
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectTemplate(template);
                      }}
                    >
                      Usar este template
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-blue-50 dark:bg-blue-900/20">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>💡 Dica:</strong> Ao selecionar um template, o fluxo atual será substituído.
            Certifique-se de salvar seu trabalho antes de importar um template.
          </p>
        </div>
      </div>
    </div>
  );
};
